/**
 * Ce qu'on vérifie avant d'émettre une facture.
 *
 * L'émission est la seule action vraiment irréversible du CRM : elle attribue
 * un numéro définitif et verrouille la facture (NF525). Une erreur ne se
 * corrige plus qu'en émettant un avoir — et le 06/10/2026, cinq des trente
 * factures émises avaient fini annulées ainsi. Ce module dit, avant le clic,
 * ce qui empêcherait la facture d'être juste.
 *
 * Deux niveaux, et la différence compte :
 *
 *  - **bloquant** : la facture serait fausse à coup sûr, et il n'y aurait plus
 *    rien à faire qu'un avoir. L'émission est refusée tant que ce n'est pas
 *    corrigé — c'est toujours une correction de deux minutes, faite AVANT.
 *  - **attention** : souvent une erreur, parfois voulu. On le montre, on
 *    demande de confirmer qu'on l'a vu, et on laisse émettre.
 */

// ----- L'adresse figée sur la facture ----------------------------------------

export interface AdresseCandidate {
  usage: string | null;
  est_default: boolean;
  ligne1: string | null;
  code_postal: string | null;
  ville: string | null;
  created_at?: string | null;
  deleted_at?: string | null;
}

/**
 * L'adresse que l'émission figera sur la facture.
 *
 * La règle est celle de la base (fonction `emettre_acompte`) : on écarte les
 * adresses supprimées, puis on prend celle d'usage « facturation », sinon celle
 * marquée par défaut, sinon la plus ancienne. Le CRM en appliquait trois
 * variantes : la fiche client pouvait modifier une adresse pendant que la
 * facture en figeait une autre, et l'émission d'une facture entière pouvait
 * figer une adresse que l'on avait SUPPRIMÉE de la fiche.
 */
export function adresseDeFacturation<T extends AdresseCandidate>(adresses: readonly T[]): T | null {
  const rangees = adresses
    .map((a, i) => ({ a, i }))
    .filter(({ a }) => a.deleted_at === null || a.deleted_at === undefined)
    .sort(
      (x, y) =>
        Number(y.a.usage === 'facturation') - Number(x.a.usage === 'facturation') ||
        Number(y.a.est_default) - Number(x.a.est_default) ||
        (x.a.created_at ?? '').localeCompare(y.a.created_at ?? '') ||
        x.i - y.i,
    );
  return rangees[0]?.a ?? null;
}

/** Ce qui manque à une adresse pour figurer sur une facture, dans l'ordre d'écriture. */
export function manquesAdresse(a: AdresseCandidate | null): string[] {
  if (a === null) return ['la rue', 'le code postal', 'la ville'];
  const manques: string[] = [];
  if ((a.ligne1 ?? '').trim() === '') manques.push('la rue');
  if ((a.code_postal ?? '').trim() === '') manques.push('le code postal');
  if ((a.ville ?? '').trim() === '') manques.push('la ville');
  return manques;
}

// ----- Les contrôles -----------------------------------------------------------

export type NiveauPoint = 'bloquant' | 'attention';

export interface PointControle {
  niveau: NiveauPoint;
  texte: string;
}

export interface EntreeControle {
  client: {
    typePersonne: string;
    email: string | null;
    siret: string | null;
  } | null;
  /** L'adresse que l'émission figera — `adresseDeFacturation` des adresses du client. */
  adresse: AdresseCandidate | null;
  nbLignes: number;
  montantTtc: number;
  /** Ce que la facture facturerait au-delà du reste à facturer du chantier, en HT. */
  depassementHt: number | null;
  /** Émise en acompte : elle ne facture qu'une part, le dépassement ne s'applique pas. */
  acompte: boolean;
}

const EURO = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

/** « la rue, le code postal et la ville » */
function enumerer(mots: readonly string[]): string {
  if (mots.length <= 1) return mots.join('');
  return `${mots.slice(0, -1).join(', ')} et ${mots[mots.length - 1] ?? ''}`;
}

export function controlesAvantEmission(e: EntreeControle): PointControle[] {
  const points: PointControle[] = [];

  // ----- Bloquants : la facture serait fausse à coup sûr ---------------------
  if (e.client === null) {
    points.push({ niveau: 'bloquant', texte: 'La facture n’a pas de client.' });
  }
  if (e.adresse === null) {
    points.push({
      niveau: 'bloquant',
      texte:
        'Le client n’a aucune adresse : la facture partirait sans, et une facture sans ' +
        'l’adresse du client n’est pas régulière. Ajoutez-la sur la fiche du client.',
    });
  } else {
    const manques = manquesAdresse(e.adresse);
    if (manques.length > 0) {
      points.push({
        niveau: 'bloquant',
        texte:
          `L’adresse du client est incomplète : il manque ${enumerer(manques)}. ` +
          'Complétez-la sur la fiche du client — elle sera figée sur la facture.',
      });
    }
  }
  if (e.nbLignes === 0) {
    points.push({ niveau: 'bloquant', texte: 'La facture n’a aucune ligne.' });
  }

  // ----- Attention : souvent une erreur, parfois voulu ------------------------
  if (e.nbLignes > 0 && e.montantTtc <= 0) {
    points.push({
      niveau: 'attention',
      texte: `Montant de ${EURO.format(e.montantTtc)} : une facture à zéro ou négative est presque toujours une erreur.`,
    });
  }
  if (!e.acompte && e.depassementHt !== null && e.depassementHt > 0.005) {
    points.push({
      niveau: 'attention',
      texte:
        `Elle facture ${EURO.format(e.depassementHt)} HT de plus que ce qu’il reste à facturer ` +
        'sur ce chantier — voir le détail plus haut.',
    });
  }
  if (e.client !== null && (e.client.email ?? '').trim() === '') {
    points.push({
      niveau: 'attention',
      texte: 'Le client n’a pas d’adresse email : il faudra lui remettre la facture autrement.',
    });
  }
  if (e.client !== null && e.client.typePersonne === 'pro' && (e.client.siret ?? '').trim() === '') {
    points.push({ niveau: 'attention', texte: 'Client professionnel sans numéro SIRET renseigné.' });
  }

  return points;
}
